import { colors } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import * as Sentry from "@sentry/browser";
import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";

// @ts-ignore
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-sql";
import { useAlert } from "react-alert";
import Editor from "react-simple-code-editor";
import useCurrentEnvironment from "../../hooks/use-environment";
import { makeRequest } from "../../util";
import { ValueType } from "react-select";
import { TransformOperationOptionType } from "../types";

const useSQLEditorStyles = makeStyles(() => ({
  saveButton: {
    "&:disabled": {
      backgroundColor: colors.grey[600],
      color: "black",
    },
  },
}));

export const SQLEditor: React.FC<{
  initialQuery: string | null;
  queryType: ValueType<TransformOperationOptionType>;
  onClose: () => void;
  onSave: (query: string) => void;
}> = ({ initialQuery, queryType, onClose, onSave }) => {
  const customStyles = {
    content: {
      zIndex: 10050,
      minHeight: `20vh`,
      width: 600,
      top: "auto",
      bottom: "auto",
      right: "auto",
      left: "auto",
      position: "static" as "static",
      display: "flex",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, .8)",
      zIndex: 10000,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  };

  const alert = useAlert();
  const classes = useSQLEditorStyles();
  const currentEnvironment = useCurrentEnvironment();
  const [editorQuery, setEditorQuery] = useState(initialQuery || "");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const proteusEnabled = !!currentEnvironment.project.config[
    "sqlTransformations"
  ];

  useEffect(() => {
    if (!proteusEnabled) {
      setErrorMessage(
        `Contact support@freshpaint.io to enable this feature for the ${currentEnvironment.project.name} project. Provide your project ID: ${currentEnvironment.project.ID}.`
      );
    }
  }, []);

  function handleSave() {
    makeRequest<{ error_message?: string }>("test_sql_transformation", {
      query: editorQuery,
      // @ts-ignore
      queryType: queryType?.value,
    })
      .then((resp) => {
        if (resp.data.error_message) {
          setErrorMessage(resp.data.error_message);
        } else {
          onSave(editorQuery);
        }
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to save transformation.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  return (
    <ReactModal
      isOpen={true}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Edit Custom SQL Transformation"
    >
      <div className="flex flex-col justify-between w-full">
        <div>
          <h2 className="text-xl text-gray-900">
            Edit Custom SQL Transformation
          </h2>
          <p className="text-xs text-gray-700">
            The provided SQL is run against the incoming event. The incoming
            event is represented as a table <code>incoming_event</code>. The
            fields returned by the query are attached as properties to the
            event. To see the schema of the incoming_event table and example SQL
            transformations, check out the{" "}
            <a
              className="text-blue-500"
              href="https://documentation.freshpaint.io/transformations/sql"
            >
              docs on Custom SQL Transformations
            </a>
            .
          </p>
          <Editor
            className="m-4 border-2 bg-gray-100 px-2 py-3 text-gray-900 font-mono text-xs"
            padding={12}
            style={{ minHeight: 120 }}
            onValueChange={setEditorQuery}
            value={editorQuery}
            placeholder={
              "SELECT 'example_value' AS example_property FROM incoming_event;"
            }
            highlight={(code) => highlight(code, languages.sql)}
          />
          <p className="text-xs text-red-600">{errorMessage}</p>
        </div>
        <div className="flex space-between mt-5">
          <button
            className="w-full rounded btn py-2 text-center bg-red-200 text-red-900 hover:bg-red-300"
            onClick={onClose}
          >
            Cancel
          </button>
          <div className="w-4 flex-shrink-0 flex-grow-0" />
          <button
            className={
              clsx(classes.saveButton) +
              " rounded w-full btn py-2 text-center bg-purple-200 text-purple-900 hover:bg-purple-300"
            }
            onClick={() => handleSave()}
            disabled={!proteusEnabled}
          >
            Save
          </button>
        </div>
      </div>
    </ReactModal>
  );
};
